import { useMemo } from "react";
import { FormatNumber } from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import type { Currency } from "@krea/shared/types/common";
import { formatLenderMonthlyInterestRate } from "@krea/shared/utils/formatNumber";

import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type { IPurchaseFinancingBid } from "@/features/lender-responses/types/lender-response";

import type { IBidDetailsResponse } from "../../BidDetails";
import { BidDetailsListItem, EmptyField } from "../../bidHelpers";

interface IPurchaseFinancingBidProps {
  bid: IPurchaseFinancingBid;
  currency: Currency;
}

export const PurchaseFinancingBid = ({
  bid,
  currency,
}: IPurchaseFinancingBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const averageMonthlyCost = bid.averageMonthlyCost;
  const interestMonthly = bid.comparableMonthlyInterestRate;
  const averageMonthlyPayment = bid.averageMonthlyPayment;

  const details = useMemo(() => {
    return (
      <div>
        {!!bid.amount && (
          <BidDetailsListItem
            title="Amount"
            value={formattedCurrencyValue(bid.amount)}
          />
        )}

        {!!bid.lenderMonthlyInterest && (
          <BidDetailsListItem
            title={
              isInterestRatePerYearActivated
                ? "Interest (per year)"
                : "Interest (per month)"
            }
            value={`${formatLenderMonthlyInterestRate(bid.lenderMonthlyInterest, "TO_YEARLY", isInterestRatePerYearActivated)}% / ${isInterestRatePerYearActivated ? "year" : "30 days"}`}
          />
        )}

        {!!bid.paybackTimeDays && (
          <BidDetailsListItem
            title="Payback time (days)"
            value={`${bid.paybackTimeDays} days`}
          />
        )}

        {!!bid.limitFeePerYear && (
          <BidDetailsListItem
            title="Limit fee (per year)"
            value={`${bid.limitFeePerYear}%`}
          />
        )}

        {!!averageMonthlyCost && (
          <BidDetailsListItem
            title="Interest payment"
            value={formattedCurrencyValue(averageMonthlyCost)}
          />
        )}

        {!!bid.setupFee && (
          <BidDetailsListItem
            title="Setup fee"
            value={formattedCurrencyValue(bid.setupFee)}
          />
        )}

        {!!bid.administrationFees && (
          <BidDetailsListItem
            title="Administration fees (per month)"
            value={formattedCurrencyValue(bid.administrationFees)}
          />
        )}
      </div>
    );
  }, [
    bid,
    formattedCurrencyValue,
    averageMonthlyCost,
    isInterestRatePerYearActivated,
  ]);

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Monthly payment"
            value={
              averageMonthlyPayment ? (
                formattedCurrencyValue(averageMonthlyPayment)
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Payback time (days)"
            value={
              bid.paybackTimeDays ? (
                `${bid.paybackTimeDays} days`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              interestMonthly ? (
                `${FormatNumber(interestMonthly)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [bid, formattedCurrencyValue, averageMonthlyPayment, interestMonthly]);

  return {
    summary,
    details,
  };
};
